import qs from 'qs';
import get from 'lodash/get';

import logger from '../../../../logger';

import {
  addToCart as addToCartFacade,
  editCartItem as editCartItemFacade,
  removeCartItem as removeCartItemFacade,
  getCart as getEagleCart,
  applyPromoCode,
  removePromoCode,
} from '../../facades/eagle/cart';
import {
  getShippingMethods as getEagleShippingMethods,
  submitShippingMethods,
  getPaymentMethods as getEaglePaymentMethods, updateAmazonAddress,
} from 'src/common/facades/eagle/checkout';
import { getCookies } from 'src/client/js/facades/eagle/helpers/cookies';
import {
  addToCartPending,
  addToCartSuccess,
  addToCartFailure,
  editCartItemPending,
  editCartItemSuccess,
  editCartItemFailure,
  removeCartItemPending,
  removeCartItemSuccess,
  removeCartItemFailure,
  getCartPending,
  getCartSuccess,
  getCartFailure,
  getCartEmpty,
  addPromoCodeSuccess,
  addPromoCodeFailure,
  removePromoCodeSuccess,
  removePromoCodeFailure,
  updateShippingMethodsSuccess,
  updateShippingMethodsFailure,
  getShippingMethodsSuccess,
  getShippingMethodsFailure,
  getShippingMethodsPending,
  getPaymentMethodsPending,
  getPaymentMethodsSuccess,
  getPaymentMethodsFailure
} from '../actions/cart';
import { getEstimatedCountry } from 'src/client/js/store/selectors/cart';
import { localiseUrl } from 'src/common/utils/format-localised-url';
import cookieFactory from '../../cookieFactory';
import { addFlashMessageError, removeFlashMessage } from '../../view/components/FlashMessages/duck';
import { trackErrors } from '../../analytics/category/checkout';

export const guestTokenCookie = cookieFactory('guestToken', { path: '/', expires: 365 });
export const itemAddedCookie = cookieFactory('item_added', { path: '/' }, true);
export const restrictedUserCookie = cookieFactory('restricted_user_id', { path: '/' });

// Handle errors that aren't RestResponseErrors
const getError = error => {
  if (!error || !error.message || error.message.match('RestResponseError')) {
    return error;
  }
  return {
    ok: false,
    status: 500,
    reason: `${error.name} ${error.message}`,
  };
};

/**
 *
 * @param {function(*): *} fn
 */
const shippingMethodsFetch = (fn) => async dispatch => {
  await dispatch(getShippingMethodsPending());
  return await fn(dispatch);
};

/**
 *
 * @param {Error} error the original error that was cause by a resource
 * @param {function(*): *} retryFunction a function that gets called while retries is not 0
 * @param {Object} config
 * @param {function} config.sendErrorMessage the translations for all the errors provided by Prismic
 * @param {CountryModel} config.countryModel the current country model based on the website url's country prefix
 * @param {number} config.retries the number of retires to fetch the resource before actually throwing an error
 */
const handleShippingMethodsError = (error, retryFunction, { sendErrorMessage, countryModel, retries = 0 } = {}) =>
  dispatch => {
    const errors = get(error, 'details.errors') || get(error, 'details') || [];
    const errorObject = errors[0] || {};
    let errorMessage = errorObject.message || errorObject.detail;

    switch (error.status) {
      case 400: {
        // returns 400 when there is no user data from shipping-details form (type: no_address)
        if (errorObject.code === 'no_address') {
          errors.map(error => logger.warn(`Missing shipping details - ${error.message}`));
          return window.location = localiseUrl(countryModel, `/checkout-v2/address`);
        }

        break;
      }
      case 404: {
        // returns 404 when there is no cart (type: no_cart)
        errors.map(error => logger.warn(`No cart - ${error.message}`));
        return window.location = localiseUrl(countryModel, `/cart`);
      }
    }

    // in case of server error, retry until unable to retry any more and show message to user
    if (retries > 0) {
      return dispatch(retryFunction({ countryModel, sendErrorMessage, retries: retries - 1 }));
    }

    logger.error('Request failed to fetch available shipping methods', error);

    sendErrorMessage(error => error.http['500'] || errorMessage);

    return dispatch(getShippingMethodsFailure(error));
  };

export const getCart = (guestToken, currency) => dispatch => {
  dispatch(getCartPending(guestToken));
  return getEagleCart(guestToken, currency)
    .then(response => dispatch(getCartSuccess(response)))
    .catch(error => {
      if ([400, 404].includes(error.status) && error.details.errors[0].code === 'cart_not_found') {
        guestTokenCookie.remove();
        return dispatch(getCartEmpty());
      }
      return dispatch(getCartFailure(error));
    });
};

export const addToCart = (payload, countryModel, redirect = true, window = {}) =>
  (dispatch, getState) => {
    dispatch(addToCartPending(payload));
    const estimatedCountryIso = getEstimatedCountry(getState());
    return addToCartFacade(payload, { ...countryModel, iso: estimatedCountryIso || get(countryModel, 'iso', '') })
      .then(response => {
        itemAddedCookie.set();
        if (response.body.data.newGuestToken) {
          guestTokenCookie.set(response.body.data.newGuestToken);
        }
        if (redirect) {
          window.location = localiseUrl(countryModel, `/cart?product-added`);
        }
        return dispatch(addToCartSuccess(response, payload));
      })
      .catch(error => {
        error = getError(error);
        return dispatch(addToCartFailure(getError(error), payload));
      });
  };

export const editCartItem = (item, countryModel) => (dispatch, getState) => {
  dispatch(editCartItemPending(item));
  const estimatedCountryIso = getEstimatedCountry(getState());
  return editCartItemFacade(item, { ...countryModel, iso: estimatedCountryIso || get(countryModel, 'iso', '') })
    .then(response => dispatch(editCartItemSuccess(response, item.id)))
    .catch(error => dispatch(editCartItemFailure(error, item.id)));
};

export const removeCartItem = (itemId, countryModel) => (dispatch, getState) => {
  const estimatedCountryIso = getEstimatedCountry(getState());
  dispatch(removeCartItemPending(itemId));
  return removeCartItemFacade(itemId, { ...countryModel, iso: estimatedCountryIso || get(countryModel, 'iso', '') })
    .then(response => dispatch(removeCartItemSuccess(response, itemId)))
    .catch(error => {
      error = getError(error);
      return dispatch(removeCartItemFailure(error, itemId));
    });
};

export const initializeCart = options => dispatch => {
  const query = qs.parse(options.query.replace(/^\?/, ''));
  if (query.guest_token || query.guestToken) {
    guestTokenCookie.set(query.guest_token || query.guestToken);
  }
  const guestToken = guestTokenCookie.get();
  if (guestToken) {
    return dispatch(getCart(guestToken, options.currency));
  }
  const restrictedUserToken = restrictedUserCookie.get();
  if (restrictedUserToken) {
    return dispatch(getCart(null, options.currency));
  }
  return Promise.resolve(dispatch(getCartEmpty()));
};

export const addPromoCode = (code, resetForm) => dispatch => {
  return applyPromoCode(code)
    .then(response => {
      if (resetForm) {
        resetForm({ code: '' });
      }
      return dispatch(addPromoCodeSuccess(response));
    })
    .catch(error => dispatch(addPromoCodeFailure(error)));
};

export const deletePromoCode = () => dispatch => {
  return removePromoCode()
    .then(response => dispatch(removePromoCodeSuccess(response)))
    .catch(error => dispatch(removePromoCodeFailure(error)));
};

export const updateShippingMethods = (shipmentNumber, selectedShippingRateId, currency, errorMessage) =>
  async (dispatch) => {
    try {
      await submitShippingMethods({ shipmentNumber, selectedShippingRateId }, getCookies());
      await dispatch(updateShippingMethodsSuccess());
      return await dispatch(getCart(null, currency));
    } catch (error) {
      trackErrors(`Update Shipping Methods - ${get(error, 'details[0].message', error.description)}`);
      // Handling error when user tries to select shipping method, but the total price doesn't update, because of failed request.
      error.details.map(error => logger.error(`Unable to update shipping methods - ${error.message}`));
      dispatch(addFlashMessageError(errorMessage || error.details[0].message));
      return dispatch(updateShippingMethodsFailure(error));
    }
  };

/**
 * getShippingMethods - get the current shipping details based on the current checkout information
 * @param {Object} config
 * @param {CountryModel} config.countryModel the current country model based on the website url's country prefix
 * @param {Object} config.sendErrorMessage the translations for all the errors provided by Prismic
 * @param {number} config.retries the number of retires to fetch the resource before actually throwing an error
 */
export const getShippingMethods = ({ countryModel, sendErrorMessage, retries = 3 }) =>
  shippingMethodsFetch(async dispatch => {
    const cookies = getCookies();

    try {
      const { body: shippingMethods } = await getEagleShippingMethods(cookies);
      await dispatch(getCart(cookies.guestToken, countryModel.currency));
      await dispatch(removeFlashMessage());
      return await dispatch(getShippingMethodsSuccess(shippingMethods));
    } catch (error) {
      return await dispatch(
        handleShippingMethodsError(error, getShippingMethods, {
          sendErrorMessage,
          countryModel,
          retries
        })
      );
    }
  });

/**
 * updateAmazonShippingDetails - update the user's address, available shipping methods,
 * and cart based on the information provided by Amazon Pay
 * @param {string} orderReferenceId the reference to the order provided by Amazon
 * @param {string} accessToken access token to update Amazon provided by eagle's payment methods
 * @param {Object} config
 * @param {CountryModel} config.countryModel the current country model based on the website url's country prefix
 * @param {Object} config.errorTranslations the translations for all the errors provided by Prismic
 */
export const updateAmazonShippingDetails = (orderReferenceId, accessToken, config) =>
  shippingMethodsFetch(async dispatch => {
    const cookies = getCookies();
    let { countryModel, sendErrorMessage, retries = 3 } = config;

    try {
      await updateAmazonAddress(orderReferenceId, accessToken, cookies);
      const { body: shippingDetails } = await getEagleShippingMethods(cookies);
      await dispatch(getCart(cookies.guestToken, countryModel.currency));
      await dispatch(removeFlashMessage());
      return await dispatch(getShippingMethodsSuccess(shippingDetails));
    } catch (error) {
      const retryFunction = (config) => dispatch(updateAmazonShippingDetails(orderReferenceId, accessToken, config));
      return await dispatch(handleShippingMethodsError(error, retryFunction, {
        sendErrorMessage,
        countryModel,
        retries
      }));
    }
  });

export const getPaymentMethods = (cookies, countryModel, errorTranslations = {}, retries = 3) => dispatch => {
  dispatch(getPaymentMethodsPending());
  return getEaglePaymentMethods(cookies)
    .then(response => dispatch(getPaymentMethodsSuccess(response.body)))
    .catch(error => {
      let errorMessage = get(error, 'details[0]', {}).message;

      if (error.status === 404) {
        // returns 404 when there is no cart (type: no_cart)
        errorMessage = errorTranslations.noCart || errorMessage;
        dispatch(addFlashMessageError(errorMessage));
        error.details.map(error => logger.warn(`No cart - ${error.message}`));
        return window.location = localiseUrl(countryModel, `/cart`);
      }

      // in case of server error, retry until unable to retry any more and show message to user
      if (retries > 0) {
        return dispatch(getPaymentMethods(cookies, countryModel, errorTranslations, retries - 1));
      }

      logger.error('Request failed to fetch available payment methods', error);
      errorMessage = errorTranslations.serverError || errorMessage;
      dispatch(addFlashMessageError(errorMessage));
      return dispatch(getPaymentMethodsFailure(error));
    });
};



// WEBPACK FOOTER //
// ./src/client/js/store/thunks/cart.js